import { SubmissionStatus } from '@bp/bp-graphql-types';
import { NoTasksIcon, Table, type TableColumns } from '@bp/ui-components';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { BpLink } from '../../BpLink/BpLink';
import { useAssignments } from '../hooks/useAssignments';

type AssignmentsActiveTableType = {
  uuid: string;
  title: string;
  openSubmissionsCount: number;
};

type AssignmentsActiveTableProps = {
  onNavigate: (uuid: string) => void;
};

export const AssignmentsActiveTable = ({ onNavigate }: AssignmentsActiveTableProps) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);
  const isDesktop = useIsDesktop();

  const { activeAssignments } = useAssignments({ courseUuid });

  const data: AssignmentsActiveTableType[] = activeAssignments.map((a) => ({
    uuid: a.uuid,
    title: a.title,
    openSubmissionsCount: a.submissions.filter((s) => {
      return s.status !== SubmissionStatus.New && s.status !== SubmissionStatus.Done;
    }).length,
  }));

  const columns: TableColumns<AssignmentsActiveTableType>[] = [
    {
      header: t('common.name'),
      accessorKey: 'title',
      id: 'title',
      size: 350,
      canExpand: true,
      cell: ({ row }) => (
        <BpLink value={row.original.title} onNavigate={() => onNavigate(row.original.uuid)} isEditorOfCourse={true} />
      ),
    },
    {
      header: t('common.open'),
      id: 'openSubmissionsCount',
      accessorKey: 'openSubmissionsCount',
      size: 75,
    },
  ];

  return (
    <Table<AssignmentsActiveTableType>
      canScroll
      isDesktop={isDesktop}
      data={data}
      columns={columns}
      customPadding='var(--spacing-6)'
      hideHeader={data.length === 0}
      emptyStateSettings={{
        fitParent: true,
        forcedHeight: '286px',
        size: 'small',
        subtitle: t('assignments.noAssignments'),
        title: '',
        iconColor: 'var(--color-primary-light)',
        icon: <NoTasksIcon />,
      }}
    />
  );
};
